import * as Sentry from "@sentry/react-native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef } from "react";
import { useSubscription } from "react-relay";

import { LockedSlots_atom } from "../../atoms";
import { SlotLockSubscriptionManagerSubscription } from "./__generated__/SlotLockSubscriptionManagerSubscription.graphql";

type Props = {
  agendaId: string;
  lockedUntil: string;
  timeSlotId: string;
  onLockEnded: () => void;
};

export const didStaffEndActiveLock = (
  response:
    | SlotLockSubscriptionManagerSubscription["response"]
    | null
    | undefined,
  activeSlotIds: ReadonlySet<string>
) => {
  if (!response) {
    return false;
  }

  const actorType = response.timeSlotsChanged?.actor.user?.__typename;
  const wasEndedByStaff =
    actorType === "StaffUser" || actorType === "WhooshAdminUser";
  const changedSlots = response.timeSlotsChanged?.slots ?? [];

  return (
    wasEndedByStaff &&
    changedSlots.some(
      (slot) =>
        slot?.__typename === "TimeSlot" &&
        activeSlotIds.has(slot.id) &&
        !slot.lockedUntil
    )
  );
};

export const SlotLockSubscriptionManager = ({
  agendaId,
  lockedUntil,
  timeSlotId,
  onLockEnded,
}: Props) => {
  const lockedSlots = useAtomValue(LockedSlots_atom);
  const handledLockIdentityRef = useRef<string | null>(null);
  const lockIdentity = `${timeSlotId}:${lockedUntil}`;
  const activeSlotIds = useMemo(
    () => new Set([timeSlotId, ...(lockedSlots?.slotIds ?? [])]),
    [lockedSlots?.slotIds, timeSlotId]
  );

  useEffect(() => {
    handledLockIdentityRef.current = null;
  }, [lockIdentity]);

  const config = useMemo(
    () => ({
      variables: { agendaId },
      subscription: SlotLockSubscriptionManagerSubscriptionString,
      onNext: (
        response:
          | SlotLockSubscriptionManagerSubscription["response"]
          | null
          | undefined
      ) => {
        if (
          didStaffEndActiveLock(response, activeSlotIds) &&
          handledLockIdentityRef.current !== lockIdentity
        ) {
          handledLockIdentityRef.current = lockIdentity;
          onLockEnded();
        }
      },
      onError: (error: Error) => {
        Sentry.captureException(error);
      },
    }),
    [activeSlotIds, agendaId, lockIdentity, onLockEnded]
  );

  useSubscription<SlotLockSubscriptionManagerSubscription>(config);

  return null;
};

const SlotLockSubscriptionManagerSubscriptionString = graphql`
  subscription SlotLockSubscriptionManagerSubscription($agendaId: ID!) {
    timeSlotsChanged(agendaIds: [$agendaId]) {
      actor {
        user {
          __typename
        }
      }
      slots {
        __typename
        ... on TimeSlot {
          id
          lockedUntil
        }
      }
    }
  }
`;
